import { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

// SEO-named portfolio images served from /public/portfolio/.
// Add more entries here as new images are supplied.
const IMAGES = [
  { src: "/portfolio/portrait-photographer-liverpool-1.jpg", alt: "Portrait photographer Liverpool, studio portrait by Yellow Video Production" },
  { src: "/portfolio/commercial-photographer-liverpool-3.jpg", alt: "Commercial photographer Liverpool, brand imagery by Yellow" },
  { src: "/portfolio/property-photography-liverpool-4.jpg", alt: "Property photography Liverpool, interior shoot by Yellow" },
  { src: "/portfolio/portrait-photography-liverpool-5.jpg", alt: "Portrait photography Liverpool, on location portrait by Yellow" },
  { src: "/portfolio/product-photographer-liverpool-6.jpg", alt: "Product photographer Liverpool, studio product shoot by Yellow" },
  { src: "/portfolio/beauty-product-photography-liverpool-9.jpg", alt: "Beauty product photography Liverpool, cosmetics shoot by Yellow" },
  { src: "/portfolio/product-photography-liverpool-10.jpg", alt: "Product photography Liverpool, creative product shoot by Yellow" },
  { src: "/portfolio/performance-photography-liverpool-11.jpg", alt: "Performance photography Liverpool, stage and theatre shoot by Yellow" },
  { src: "/portfolio/fashion-bridal-photographer-liverpool-12.jpg", alt: "Fashion and bridal photographer Liverpool, dress and gown shoot by Yellow" },
  { src: "/portfolio/lash-brow-product-photography-liverpool-13.jpg", alt: "Beauty product photography Liverpool, lash and brow product splash shoot by Yellow" },
  { src: "/portfolio/interior-photographer-liverpool-14.jpg", alt: "Interior photographer Liverpool, hotel and reception lobby shoot by Yellow" },
  { src: "/portfolio/wildlife-photographer-liverpool-15.jpg", alt: "Wildlife photographer Liverpool, bald eagle portrait by Yellow" },
  { src: "/portfolio/corporate-photographer-liverpool-16.jpg", alt: "Corporate photographer Liverpool, business and finance shoot by Yellow" },
  { src: "/portfolio/portrait-photographer-liverpool-17.jpg", alt: "Portrait photographer Liverpool, relaxed lifestyle portrait by Yellow" },
  { src: "/portfolio/corporate-portrait-photographer-liverpool-18.jpg", alt: "Corporate portrait photographer Liverpool, professional headshot by Yellow" },
  { src: "/portfolio/liverpool-architecture-photography-19.jpg", alt: "Liverpool architecture photography, Sefton Park Palm House shoot by Yellow" },
  { src: "/portfolio/charity-event-photographer-liverpool-20.jpg", alt: "Charity event photographer Liverpool, fundraising campaign coverage by Yellow" },
  { src: "/portfolio/industrial-photographer-liverpool-21.jpg", alt: "Industrial photographer Liverpool, on-site engineering shoot by Yellow" },
];

export default function PortfolioGallery() {
  const [openIndex, setOpenIndex] = useState(null);
  const isOpen = openIndex !== null;

  const close = useCallback(() => setOpenIndex(null), []);
  const prev = useCallback(
    () => setOpenIndex((i) => (i === null ? null : (i - 1 + IMAGES.length) % IMAGES.length)),
    []
  );
  const next = useCallback(
    () => setOpenIndex((i) => (i === null ? null : (i + 1) % IMAGES.length)),
    []
  );

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, close, prev, next]);

  return (
    <section className="yv-container !mt-16" data-testid="portfolio-gallery">
      <div className="mb-10">
        <p className="yv-eyebrow">Portfolio</p>
        <h2 className="yv-h2 text-white mt-2">Selected photography work.</h2>
        <p className="text-white/60 mt-3 max-w-2xl">
          A snapshot of recent shoots, products, portraits, fashion, events and performance, across Liverpool and the wider UK.
        </p>
      </div>

      {/* Uniform grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {IMAGES.map((img, i) => (
          <button
            key={img.src}
            type="button"
            onClick={() => setOpenIndex(i)}
            className="block w-full aspect-square overflow-hidden rounded-md border border-white/10 bg-white/[0.03] group focus:outline-none focus:ring-2 focus:ring-[var(--yellow)]"
            data-testid={`portfolio-thumb-${i}`}
            aria-label={`Open image: ${img.alt}`}
          >
            <img
              src={img.src}
              alt={img.alt}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
            />
          </button>
        ))}
      </div>

      {isOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
          onClick={close}
          role="dialog"
          aria-modal="true"
          data-testid="portfolio-lightbox"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); close(); }}
            className="absolute top-4 right-4 text-white/80 hover:text-[var(--yellow)] p-2"
            aria-label="Close"
            data-testid="lightbox-close"
          >
            <X size={28} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 text-white/80 hover:text-[var(--yellow)] p-2"
            aria-label="Previous image"
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={40} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 text-white/80 hover:text-[var(--yellow)] p-2"
            aria-label="Next image"
            data-testid="lightbox-next"
          >
            <ChevronRight size={40} />
          </button>
          <img
            src={IMAGES[openIndex].src}
            alt={IMAGES[openIndex].alt}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] max-w-[92vw] object-contain rounded-md shadow-2xl"
            data-testid="lightbox-image"
          />
        </div>
      )}
    </section>
  );
}
